
import Vue from "vue";
import Component from "vue-class-component";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {
    AddRepresentativeModel,
    OrganizationActions,
    OrganizationDetailsModel,
    OrganizationsClient,
    RepresentativeViewModel,
    ResultError, RoleModel,
    UpdateOrganizationContactInfoModel,
    UpdateRepresentativeModel,
    SpecialityLightModel,
    SpecialityId,
    AvailableActions
} from "@/api-client";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {factory} from "@/api-client.extensions";
import {isNullOrWhiteSpace} from "@/extensions/string.helpers";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import {Prop} from "vue-property-decorator";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {BvTableFieldArray} from "bootstrap-vue";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import toasts from "@/components/Toasts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import ImageUploader from "@/components/ImageUploader.vue";
import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
import Loader from "@/features/shared/components/Loader";
import _ from "underscore";

enum IdentifierType {
    Username = 1,
    PersonalCode = 2
}

@Component({
    components: {
        LabelOptional,
        SectionPageHeading,
        HeadingGroup,
        ButtonWithConfirm,
        HeadingWave,
        TableEmptySlot,
        PopoverInfo,
        ImageUploader,
        LoadingSpinner
    },
})
export default class OrganizationDetails extends Vue {
    @Prop({required: true})
    preloadedOrganization!: OrganizationDetailsModel;

    @Prop({required: true})
    availableActions!: OrganizationActions;

    @Prop({required: false})
    availableSpecialities!: SpecialityLightModel[];
    
    organization = factory.create(OrganizationDetailsModel);
    
    editContactInfoModel: UpdateOrganizationContactInfoModel = factory.create(UpdateOrganizationContactInfoModel);
    addRepresentativeModel: AddRepresentativeModel = this.newAddRepresentativeModel();

    representativeUpdateModel: UpdateRepresentativeModel = factory.create(UpdateRepresentativeModel);
    representativeBeingUpdated: RepresentativeViewModel = factory.create(RepresentativeViewModel);

    newRepresentativeIdentifierType: IdentifierType = IdentifierType.PersonalCode;
    updateRepresentativeIdentifierType: IdentifierType = IdentifierType.PersonalCode;
    IdentifierType: any = IdentifierType;
    RRLoader = new Loader();

    errors: ResultError = factory.create(ResultError);
    modalErrors: ResultError = factory.create(ResultError);
    
    created() {
        this.organization = this.preloadedOrganization;
    }

    get disableAddRepresentativeSubmit() {
        return isNullOrWhiteSpace(this.addRepresentativeModel.firstName)
            || isNullOrWhiteSpace(this.addRepresentativeModel.lastName)
            || (isNullOrWhiteSpace(this.addRepresentativeModel.username) && isNullOrWhiteSpace(this.addRepresentativeModel.personalCode));
    }

    get disableEditContactInfoSubmit() {
        return isNullOrWhiteSpace(this.editContactInfoModel.address)
            || isNullOrWhiteSpace(this.editContactInfoModel.phone)
            || isNullOrWhiteSpace(this.editContactInfoModel.email);
    }

    get types() {
        return (this.organization.types || []).map(x => x.name).join(", ") || "-";
    }

    roles(rolesCollection: RoleModel[]) {
        return (rolesCollection || []).map(r => r.name).join(", ") || "-";
    }

    specialities(specialitiesCollection: SpecialityId[]) {
        const specialitiesFlat = (specialitiesCollection || []).map(m => m.value);
        return this.availableSpecialities.filter(s => specialitiesFlat.includes(s.id)).map(s => s.name).join(", ") || "-";        
    }

    async reloadOrganization() {
        this.organization = await new OrganizationsClient().details(this.organization.id);
    }

    async inactivateRepresentative(id: string) {
        this.resetErrors();
        await new OrganizationsClient().inactivateRepresentative(id, this.organization.id).then(
            async () => {
                await this.reloadOrganization();
                toasts.success(this.$ls.Organizations_Toast_Success_RepresentativeDeactivated);
            },
            error => this.setValidationErrors(error, null, "errors"));
    }

    async activateRepresentative(id: string) {
        this.resetErrors();
        await new OrganizationsClient().activateRepresentative(id, this.organization.id).then(
            async () => {
                await this.reloadOrganization();
                toasts.success(this.$ls.Organizations_Toast_Success_RepresentativeActivated);
            },
            error => this.setValidationErrors(error, null, "errors"));
    }

    async updateRepresentative(model: UpdateRepresentativeModel, id: string, modalId: string) {
        this.resetModalErrors();
        await new OrganizationsClient().updateRepresentative(model, id,  this.organization.id).then(
            async () => {
                this.$bvModal.hide(modalId);
                await this.reloadOrganization();
                toasts.success(this.$ls.Organizations_Toast_Success_RepresentativeDetailsUpdated);
            },
            error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure, "modalErrors"));
    }

    async updateContactInfo() {
        this.resetModalErrors();
        await new OrganizationsClient().saveContactInfo(this.editContactInfoModel, this.organization.id).then(
            async () => {
                this.$bvModal.hide('modal-edit-organization-info');
                await this.reloadOrganization();
                toasts.success(this.$ls.Organizations_Toast_Success_OrganizationContactDetailsUpdated);
            },
            error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure, "modalErrors"));
    }

    async addRepresentative() {
        this.resetModalErrors();
        
        // Send only one OR the other identifier.
        if (this.newRepresentativeIdentifierType == IdentifierType.Username)
            this.addRepresentativeModel.personalCode = null;
        else
            this.addRepresentativeModel.username = null;
        
        await new OrganizationsClient().addRepresentative(this.addRepresentativeModel, this.organization.id).then(
            async () => {
                this.$bvModal.hide('modal-add-organization-user');
                await this.reloadOrganization();
                toasts.success(this.$ls.Organizations_Toast_Success_RepresentativeAdded);
            },
            error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure, "modalErrors"));
    }

    identifier(representative: RepresentativeViewModel) {
        return representative.username ?? representative.personalCode;
    }

    openUpdateContactInfoModal() {
        this.resetModalErrors();
        this.$bvModal.show('modal-edit-organization-info');
        this.editContactInfoModel.email = this.organization.contactInfo.email;
        this.editContactInfoModel.address = this.organization.contactInfo.address;
        this.editContactInfoModel.phone = this.organization.contactInfo.phone;
    }

    openUpdateRepresentativeModal(representative: RepresentativeViewModel) {
        this.resetModalErrors();
        this.$bvModal.show('modal-edit-organization-user');
        this.representativeUpdateModel = factory.create(UpdateRepresentativeModel);
        this.representativeUpdateModel.contactEmail = representative.contactEmail;
        this.representativeUpdateModel.phone = representative.phone;
        this.representativeUpdateModel.roles = representative.roles || [];
        this.representativeUpdateModel.firstName = representative.firstName;
        this.representativeUpdateModel.lastName = representative.lastName;
        this.representativeUpdateModel.username = representative.username;
        this.representativeUpdateModel.personalCode = representative.personalCode;
        this.updateRepresentativeIdentifierType = representative.username ? IdentifierType.Username : IdentifierType.PersonalCode;
        this.representativeBeingUpdated = _.clone(representative); // Clone to enable temporary modifications (signature image) and not persist the change.
    }


    updateRepresentativeModal(representative: RepresentativeViewModel) {
        this.representativeUpdateModel = factory.create(UpdateRepresentativeModel);
        this.representativeUpdateModel.contactEmail = representative.contactEmail;
        this.representativeUpdateModel.phone = representative.phone;
        this.representativeUpdateModel.roles = representative.roles || [];
        this.representativeUpdateModel.firstName = representative.firstName;
        this.representativeUpdateModel.lastName = representative.lastName;
        this.representativeUpdateModel.username = representative.username;
        this.representativeUpdateModel.personalCode = representative.personalCode;
        this.updateRepresentativeIdentifierType = representative.username ? IdentifierType.Username : IdentifierType.PersonalCode;
        this.representativeBeingUpdated = _.clone(representative); // Clone to enable temporary modifications (signature image) and not persist the change.
    }

    openAddRepresentativeModal() {
        this.resetModalErrors();
        this.$bvModal.show('modal-add-organization-user');
        this.addRepresentativeModel = this.newAddRepresentativeModel();
    }

    newAddRepresentativeModel(): AddRepresentativeModel {
        const model = factory.create(AddRepresentativeModel);
        model.roles = [];
        return model;
    }

    async handleUpdateRepresentative() {
        if (this.updateRepresentativeIdentifierType == IdentifierType.Username)
            this.representativeUpdateModel.personalCode = null;
        else
            this.representativeUpdateModel.username = null;
        await this.updateRepresentative(this.representativeUpdateModel, this.representativeBeingUpdated.id, 'modal-edit-organization-user');
    }


    async updateFromRR() {
         this.resetModalErrors();
        await new OrganizationsClient().setLoader(this.RRLoader).updateFromRR(this.representativeBeingUpdated.id, this.organization.id).then(
            async () => {
                await this.reloadOrganization();
                const newRepresentative = this.organization.representatives.find(x=>x.id === this.representativeBeingUpdated.id);
                
                if(newRepresentative){
                    this.updateRepresentativeModal(newRepresentative)
                }
                toasts.success(this.$ls.Organizations_Toast_Success_RepresentativeDetailsUpdated);
            },
            error => this.setValidationErrors(error, this.$ls.Global_Toast_FormSaveFailure, "modalErrors"));
    }

    resetErrors() {
        this.errors = factory.create(ResultError);
    }

    resetModalErrors() {
        this.modalErrors = factory.create(ResultError);
    }

    async getSignatureImageDownloadUrl(representativeId: string, fileId: string): Promise<string> {
        return (await new OrganizationsClient().downloadRepresentativeSignatureFile(representativeId, fileId, this.organization.id)).url;
    }

    representativeTableFieldsWithActions: BvTableFieldArray = [
        {
            key: "fullName",
            label: this.$ls.Global_Label_FullName,
            sortable: true,
            thClass: 'w-15'
        },
        {
            key: "usernameAndPersonalCode",
            label: this.$ls.Organizations_Table_Label_RepresentativeIdendifier,
            thClass: 'w-15'
        },
        {
            key: "roles",
            label: this.$ls.Organizations_Table_Label_RepresentativeRoles,
            thClass: 'w-20'
        },
        {
            key: 'contactInfo',
            label: this.$ls.Organizations_Table_Label_RepresentativeContactDetails,
            sortable: false,
            thClass: 'w-15'
        },
         {
            key: "isActive",
            label: this.$ls.Global_Label_Active,
            sortable: true,
            thClass: 'w-10',
            formatter: (
                value: string,
                key: string,
                item: RepresentativeViewModel
            ) => translateTrueFalse(item.isActive),
        },
        {
            key: 'actions',
            label: '',
            sortable: false,
            thClass: 'w-25',
            tdClass: 'table-actions'
        }
    ];

    representativeTableFields: BvTableFieldArray = [
        {
            key: "fullName",
            label: this.$ls.Global_Label_FullName,
            sortable: true,
            thClass: 'w-15'
        },
        {
            key: "usernameAndPersonalCode",
            label: this.$ls.Organizations_Table_Label_RepresentativeIdendifier,
            thClass: 'w-15'
        },
         {
            key: "roles",
            label: this.$ls.Organizations_Table_Label_RepresentativeRoles,
            thClass: 'w-20'
        },
        {
            key: 'contactInfo',
            label: this.$ls.Organizations_Table_Label_RepresentativeContactDetails,
            sortable: false,
            thClass: 'w-15'
         },
        {
            key: "isActive",
            label: this.$ls.Global_Label_Active,
            sortable: true,
            thClass: 'w-8',
            formatter: (
                value: string,
                key: string,
                item: RepresentativeViewModel
            ) => translateTrueFalse(item.isActive),
        }
    ];
}
